/**
 * InstructorNotesEditor — inline viewer/editor for the instructor's own session notes
 *
 * Shows the instructor's typed notes from sessions.instructor_notes.
 * These are the instructor's personal observations from the sim session,
 * NOT the AI-generated Part D self-reflection guidance.
 *
 * Uses the same auto-save pattern (3s debounce) to persist edits
 * back to sessions.instructor_notes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

interface InstructorNotesEditorProps {
  sessionId: string;
  /** Current instructor notes text from sessions.instructor_notes */
  instructorNotes: string | null;
  readOnly?: boolean;
}

export default function InstructorNotesEditor({
  sessionId,
  instructorNotes,
  readOnly,
}: InstructorNotesEditorProps) {
  const [localValue, setLocalValue] = useState(instructorNotes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setLocalValue(instructorNotes ?? '');
  }, [instructorNotes]);

  const saveNotes = useCallback(
    async (value: string) => {
      setIsSaving(true);
      try {
        await supabase
          .from('sessions')
          .update({ instructor_notes: value || null })
          .eq('id', sessionId);
      } catch (err) {
        console.error('[InstructorNotesEditor] Save error:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [sessionId],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => saveNotes(newValue), 3000);
    },
    [saveNotes],
  );

  // Fire save on blur (handles collapse-while-editing edge case)
  const handleBlur = useCallback(() => {
    clearTimeout(timerRef.current);
    if (localValue !== (instructorNotes ?? '')) {
      saveNotes(localValue);
    }
  }, [localValue, instructorNotes, saveNotes]);

  const hasContent = !!localValue.trim();

  return (
    <div className="bg-white border border-steel/15 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-navy">Instructor Notes</h4>
        {isSaving && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
            Saving...
          </span>
        )}
      </div>
      <p className="text-xs text-steel mb-2">
        Your personal observations and notes from the session.
      </p>

      {readOnly ? (
        hasContent ? (
          <p className="text-sm text-navy whitespace-pre-wrap bg-ice rounded p-3">
            {localValue}
          </p>
        ) : (
          <p className="text-sm text-steel italic">No instructor notes for this session.</p>
        )
      ) : (
        <textarea
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Add your notes from the session here..."
          rows={4}
          className="w-full text-sm border border-steel/20 rounded-md p-2 resize-y
            focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40"
        />
      )}
    </div>
  );
}
